import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Database, MessageSquare, Clock, Cpu, Shield, Brain, BookmarkPlus, RefreshCw, Settings2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PostMortemTrade {
  id: string;
  symbol: string;
  side: string;
  average_entry_price: number;
  realized_pnl: number;
  opened_at: string;
  closed_at: string | null;
  /** Optional link back to the strategy that produced this trade. When omitted,
   *  the "Update Rules" and "Retrain Model" buttons are disabled. */
  strategy_instance_id?: string | null;
}

interface AnalysisAgent {
  name: string;
  icon: React.ElementType;
  status: 'ok' | 'warning' | 'error';
  finding: string;
}

function analyzeFailure(trade: PostMortemTrade): { agents: AnalysisAgent[]; logs: { time: string; agent: string; msg: string; level: 'ok' | 'warning' | 'error' }[] } {
  const agents: AnalysisAgent[] = [];
  const logs: { time: string; agent: string; msg: string; level: 'ok' | 'warning' | 'error' }[] = [];
  const lossAbs = Math.abs(trade.realized_pnl);
  const entryDate = new Date(trade.opened_at);
  const dayOfWeek = entryDate.getDay();

  // Data Agent
  const isSmallLoss = lossAbs < 50;
  agents.push({
    name: 'Data',
    icon: Database,
    status: isSmallLoss ? 'ok' : 'warning',
    finding: isSmallLoss ? 'Sufficient historical data' : 'Historical data may be sparse',
  });
  logs.push({ time: entryDate.toLocaleTimeString(), agent: 'DATA', msg: isSmallLoss ? 'Historical data adequate' : 'Limited price history for this regime', level: isSmallLoss ? 'ok' : 'warning' });

  // Sentiment Agent
  const isFriday = dayOfWeek === 5;
  agents.push({
    name: 'Sentiment',
    icon: MessageSquare,
    status: isFriday ? 'error' : 'ok',
    finding: isFriday ? 'Weekend risk signals missed' : 'Sentiment aligned with entry',
  });
  logs.push({ time: entryDate.toLocaleTimeString(), agent: 'SENTIMENT', msg: isFriday ? 'Bearish weekend signals detected post-entry' : 'Social sentiment was neutral-positive', level: isFriday ? 'error' : 'ok' });

  // Timing Agent
  const isLargeLoss = lossAbs > 200;
  agents.push({
    name: 'Timing',
    icon: Clock,
    status: isLargeLoss ? 'error' : 'warning',
    finding: isLargeLoss ? 'Entry near major event' : 'Timing within normal range',
  });
  logs.push({ time: entryDate.toLocaleTimeString(), agent: 'TIMING', msg: isLargeLoss ? 'Entry 48hrs before major policy change detected' : 'Entry timing within acceptable window', level: isLargeLoss ? 'error' : 'warning' });

  // Model Agent
  agents.push({
    name: 'Model',
    icon: Cpu,
    status: lossAbs > 100 ? 'warning' : 'ok',
    finding: lossAbs > 100 ? 'Missing regulatory feature' : 'Model features adequate',
  });
  logs.push({ time: entryDate.toLocaleTimeString(), agent: 'MODEL', msg: lossAbs > 100 ? 'No feature for regulatory changes in model' : 'Model prediction was within expected range', level: lossAbs > 100 ? 'warning' : 'ok' });

  // Risk Agent
  agents.push({
    name: 'Risk',
    icon: Shield,
    status: lossAbs > 300 ? 'error' : 'ok',
    finding: lossAbs > 300 ? 'Geopolitical flag ignored' : 'Risk limits respected',
  });
  logs.push({ time: entryDate.toLocaleTimeString(), agent: 'RISK', msg: lossAbs > 300 ? 'Geopolitical risk flag was active but overridden' : 'Position size within risk parameters', level: lossAbs > 300 ? 'error' : 'ok' });

  return { agents, logs };
}

const statusDot: Record<string, string> = {
  ok: 'bg-[hsl(var(--bull))]',
  warning: 'bg-[hsl(var(--warning))]',
  error: 'bg-[hsl(var(--bear))]',
};

const logColor: Record<string, string> = {
  ok: 'text-[hsl(var(--bull))]',
  warning: 'text-[hsl(var(--warning))]',
  error: 'text-[hsl(var(--bear))]',
};

export const TradePostMortem: React.FC<{ trade: PostMortemTrade }> = ({ trade }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<null | 'save' | 'rules' | 'retrain'>(null);
  const { user } = useAuth();
  const { agents, logs } = analyzeFailure(trade);

  const hasStrategy = Boolean(trade.strategy_instance_id);

  const handleSave = async () => {
    if (!user?.id) {
      toast.error('You must be signed in to save a post-mortem.');
      return;
    }
    setBusy('save');
    try {
      const { error } = await supabase.from('notifications').insert({
        user_id: user.id,
        type: 'post_mortem',
        title: `Post-mortem: ${trade.symbol} ${trade.side.toUpperCase()}`,
        message: `Realized PnL: ${trade.realized_pnl >= 0 ? '+' : ''}${trade.realized_pnl.toFixed(2)}. Lesson logged for future review.`,
        read: false,
      });
      if (error) throw error;
      toast.success('Post-mortem saved', { description: 'Available in your notifications center.' });
    } catch (e: any) {
      console.error('post-mortem save failed', e);
      toast.error('Could not save post-mortem', { description: e?.message ?? 'Unknown error' });
    } finally {
      setBusy(null);
    }
  };

  const handleUpdateRules = async () => {
    if (!trade.strategy_instance_id) return;
    setBusy('rules');
    try {
      const { error } = await supabase
        .from('strategy_instances')
        .update({ risk_mode: 'conservative' })
        .eq('id', trade.strategy_instance_id);
      if (error) throw error;
      toast.success('Risk rules updated', { description: 'Strategy switched to conservative mode.' });
    } catch (e: any) {
      console.error('update risk rules failed', e);
      toast.error('Could not update risk rules', { description: e?.message ?? 'Unknown error' });
    } finally {
      setBusy(null);
    }
  };

  const handleRetrain = async () => {
    if (!trade.strategy_instance_id) return;
    setBusy('retrain');
    try {
      const { error } = await supabase
        .from('strategy_performance')
        .update({ needs_retrain: true })
        .eq('strategy_instance_id', trade.strategy_instance_id);
      if (error) throw error;
      toast.success('Strategy queued for review', { description: 'Marked for retrain on the next training cycle.' });
    } catch (e: any) {
      console.error('queue retrain failed', e);
      toast.error('Could not queue retrain', { description: e?.message ?? 'Unknown error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))] accent-border-loss">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))]" />
            <div>
              <CardTitle className="text-sm">Post-Mortem Analysis</CardTitle>
              <p className="text-[10px] text-muted-foreground">Learning from failed predictions</p>
            </div>
          </div>
          <Badge variant="outline" className="text-[hsl(var(--bear))] border-[hsl(var(--bear)/0.3)]">
            {trade.side.toUpperCase()} Failed
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trade Summary */}
        <div className="flex items-center gap-4 text-xs">
          <span className="font-mono-data font-semibold text-foreground">{trade.symbol}</span>
          <span className="text-muted-foreground">Entry: ${trade.average_entry_price.toFixed(2)}</span>
          <span className="font-mono-data font-bold text-[hsl(var(--bear))]">
            {trade.realized_pnl >= 0 ? '+' : ''}${trade.realized_pnl.toFixed(2)}
          </span>
          <span className="text-muted-foreground">{new Date(trade.opened_at).toLocaleDateString()}</span>
        </div>

        {/* Analysis Agents */}
        <div className="grid grid-cols-5 gap-2">
          {agents.map(a => (
            <div key={a.name} className="flex flex-col items-center gap-1 p-2 rounded-lg bg-[hsl(var(--background-tertiary))] text-center">
              <a.icon className="w-3.5 h-3.5 text-muted-foreground" />
              <div className="flex items-center gap-1">
                <div className={`w-1.5 h-1.5 rounded-full ${statusDot[a.status]}`} />
                <span className="text-[10px] font-semibold text-foreground">{a.name}</span>
              </div>
              <span className="text-[9px] text-muted-foreground leading-tight">{a.finding}</span>
            </div>
          ))}
        </div>

        {/* Analysis Log */}
        <div className="rounded-lg bg-background p-3 max-h-32 overflow-auto">
          {logs.slice(0, expanded ? undefined : 4).map((log, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px] font-mono-data py-0.5">
              <span className="text-muted-foreground shrink-0">{log.time}</span>
              <span className={`font-semibold shrink-0 ${logColor[log.level]}`}>[{log.agent}]</span>
              <span className="text-muted-foreground">{log.msg}</span>
            </div>
          ))}
          {logs.length > 4 && (
            <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-primary mt-1">
              {expanded ? 'Show less' : `Show ${logs.length - 4} more...`}
            </button>
          )}
        </div>

        {/* System Updates */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1"
            onClick={handleSave}
            disabled={busy !== null || !user?.id}
          >
            {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3" />}
            Save to Memory
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1"
            onClick={handleUpdateRules}
            disabled={busy !== null || !hasStrategy}
            title={hasStrategy ? undefined : 'No linked strategy for this trade'}
          >
            {busy === 'rules' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Settings2 className="w-3 h-3" />}
            Update Rules
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1"
            onClick={handleRetrain}
            disabled={busy !== null || !hasStrategy}
            title={hasStrategy ? undefined : 'No linked strategy for this trade'}
          >
            {busy === 'retrain' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Retrain Model
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TradePostMortem;
